import React, { useState, useEffect, useCallback, useRef } from "react";
import { getEpg, moveSlot, undoLastOp, listChannels } from "../api";
import { useParams, Link as RouterLink } from "react-router-dom";
import EpgEditor, { EpgItemDto } from "../components/EpgEditor";
import {
  Box, Button, Typography, IconButton,
  ToggleButton, ToggleButtonGroup, Paper, Tooltip,
  Menu, MenuItem, ListItemIcon, ListItemText, Divider,
  Snackbar, Alert, Stack
} from "@mui/material";
import {
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut,
  AccessTime, Edit as EditIcon,
  Add as AddIcon,
  Lock as LockIcon,
  KeyboardOutlined as KeyboardIcon,
} from "@mui/icons-material";

type Row = { channel_id: string; channel_name: string; items: EpgItemDto[] };

// Zoom levels in hours
const ZOOM_LEVELS = [
  { label: "2H", hours: 2, showText: true, textSize: 12 },
  { label: "4H", hours: 4, showText: true, textSize: 11 },
  { label: "6H", hours: 6, showText: true, textSize: 10 },
  { label: "12H", hours: 12, showText: true, textSize: 8 },
  { label: "1D", hours: 24, showText: false, textSize: 0 },
  { label: "2D", hours: 48, showText: false, textSize: 0 },
];

// Per-zoom grid: snap (drop/nav granularity) and label spacing.
// Snap is 5 min at 2H/4H, 15 min everywhere else.
function getGridConfig(hours: number) {
  if (hours <= 2)  return { snapMin: 5,  labelStepMin: 15  };
  if (hours <= 4)  return { snapMin: 5,  labelStepMin: 30  };
  if (hours <= 6)  return { snapMin: 15, labelStepMin: 30  };
  if (hours <= 12) return { snapMin: 15, labelStepMin: 60  };
  if (hours <= 24) return { snapMin: 15, labelStepMin: 120 };
  return                  { snapMin: 15, labelStepMin: 240 };
}

function snapToMinutes(date: Date, minutes: number): Date {
  const ms = minutes * 60_000;
  return new Date(Math.round(date.getTime() / ms) * ms);
}

// Threshold (px) past which a mousedown becomes a drag rather than a click.
const DRAG_THRESHOLD_PX = 4;

type DragRef = {
  item: EpgItemDto;
  trackEl: HTMLElement;
  channel: string;
  startX: number;
  startY: number;
  moved: boolean;
};

export default function EPGEnhanced() {
  const [rows, setRows] = useState<Row[]>([]);
  const [baseTime, setBaseTime] = useState<Date>(snapToMinutes(new Date(), 15));
  const [zoomLevel, setZoomLevel] = useState(2);
  const [editing, setEditing] = useState<{ item: EpgItemDto; siblings: EpgItemDto[]; mode: "edit" | "create" } | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [draggedItem, setDraggedItem] = useState<EpgItemDto | null>(null);
  const [dropIndicator, setDropIndicator] = useState<{ percent: number; iso: string } | null>(null);
  // Undo toast for the last create_slot / move_slot op.
  const [toast, setToast] = useState<{ message: string; opId: string } | null>(null);
  // Drill-down: when route is /epg/:channel, show only that channel + a detail panel.
  const { channel: channelParam } = useParams<{ channel?: string }>();
  // Per-channel write permission (from /api/channels). Backend is the source of
  // truth; this only hides affordances. Unknown channel -> allow (backend gates).
  const [writable, setWritable] = useState<Record<string, boolean>>({});
  const canWrite = (ch?: string) => (ch ? (writable[ch] ?? true) : true);

  // Mutable drag-in-progress state — avoids React-render lag for movement.
  const dragRef = useRef<DragRef | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const gridPaperRef = useRef<HTMLDivElement>(null);
  // Accumulates fractional wheel/trackpad deltaX between shifts so small swipes
  // still add up to a whole snap step.
  const wheelAccumRef = useRef(0);

  // Right-click context menu state: pixel position + which time was under the cursor.
  // If the user right-clicked directly on a mediapoint, `item` is set and the
  // menu offers "Edit MediaPoint" as well.
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; time: Date; channel?: string; item?: EpgItemDto } | null>(null);
  // Last cursor time over the grid (updated on mousemove). Used by keyboard
  // zoom/center shortcuts so they anchor at the cursor like the right-click menu.
  const lastCursorTimeRef = useRef<Date | null>(null);
  const currentZoom = ZOOM_LEVELS[zoomLevel];
  const grid = getGridConfig(currentZoom.hours);

  const getTimeWindow = useCallback(() => {
    const start = new Date(baseTime);
    const end = new Date(baseTime);
    end.setHours(end.getHours() + currentZoom.hours);
    return { start, end };
  }, [baseTime, currentZoom]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { start, end } = getTimeWindow();
      const data = await getEpg(start.toISOString(), end.toISOString());
      setRows(data || []);
    } catch (error) {
      console.error("Failed to load EPG data:", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [getTimeWindow]);

  useEffect(() => { load(); }, [load]);

  // Load channel write permissions once for affordance gating.
  useEffect(() => {
    listChannels()
      .then((cs) => setWritable(Object.fromEntries(cs.map((c) => [c.name, c.can_write]))))
      .catch(() => { /* leave defaults (allow); backend still enforces */ });
  }, []);

  // Undo the last toasted op (create_slot / move_slot), then reload.
  const doUndo = useCallback(async () => {
    if (!toast) return;
    try { await undoLastOp(toast.opId); await load(); }
    catch (e) { console.error("Undo failed:", e); }
    finally { setToast(null); }
  }, [toast, load]);

  // Nav handlers
  const slideTime = (direction: 'left' | 'right') => {
    const hours = direction === 'left' ? -currentZoom.hours : currentZoom.hours;
    setBaseTime(prev => {
      const t = new Date(prev);
      t.setHours(t.getHours() + hours);
      return snapToMinutes(t, grid.snapMin);
    });
  };
  const handleZoom = (delta: number) => {
    setZoomLevel(prev => Math.max(0, Math.min(ZOOM_LEVELS.length - 1, prev + delta)));
  };
  const goToNow = () => setBaseTime(snapToMinutes(new Date(), grid.snapMin));

  // Zoom in/out while keeping `cursorTime` at the same visual x within the
  // window. Used by the right-click context menu.
  const zoomAtCursor = (delta: number, cursorTime: Date) => {
    const newIdx = Math.max(0, Math.min(ZOOM_LEVELS.length - 1, zoomLevel + delta));
    if (newIdx === zoomLevel) return;
    const { start: oldStart, end: oldEnd } = getTimeWindow();
    const oldDur = oldEnd.getTime() - oldStart.getTime();
    const pct = Math.max(0, Math.min(1, (cursorTime.getTime() - oldStart.getTime()) / oldDur));
    const newHours = ZOOM_LEVELS[newIdx].hours;
    const newDur = newHours * 3600 * 1000;
    const newBaseMs = cursorTime.getTime() - pct * newDur;
    const newGrid = getGridConfig(newHours);
    setZoomLevel(newIdx);
    setBaseTime(snapToMinutes(new Date(newBaseMs), newGrid.snapMin));
  };

  // Shift baseTime so `cursorTime` lands in the middle of the visible window.
  const centerOnCursor = (cursorTime: Date) => {
    const halfMs = (currentZoom.hours * 3600 * 1000) / 2;
    setBaseTime(snapToMinutes(new Date(cursorTime.getTime() - halfMs), grid.snapMin));
  };

  // Open the editor for `item`, also gathering its slot siblings (same channel,
  // action, starts_at) so slot-wide changes propagate.
  const openEditorFor = (item: EpgItemDto) => {
    const row = rows.find((r) => r.channel_name === item.channel);
    const sibs = row
      ? row.items.filter((it) => it.action === item.action && it.starts_at === item.starts_at)
      : [item];
    setEditing({ item, siblings: sibs, mode: "edit" });
    setEditorOpen(true);
  };

  // Open the editor in CREATE mode at `time` on `channel`. Pre-fills with
  // sensible defaults; user can change anything before saving.
  const openEditorForNew = (channel: string, time: Date) => {
    // Snap the start time to the current grid so the new item aligns with ticks
    const snapped = snapToMinutes(time, grid.snapMin);
    // Default duration: 60 min unless the snap grid is finer, then 30 min
    const defaultDuration = grid.snapMin <= 5 ? "PT30M" : "PT1H";
    const durMs = defaultDuration === "PT30M" ? 30 * 60_000 : 60 * 60_000;
    const draft: EpgItemDto = {
      id: `new|${channel}|${snapped.toISOString()}`,
      title: "",
      starts_at: snapped.toISOString(),
      ends_at: new Date(snapped.getTime() + durMs).toISOString(),
      status: "scheduled",
      audience: "all",
      action: "",
      channel,
      kind: "program",
    };
    setEditing({ item: draft, siblings: [draft], mode: "create" });
    setEditorOpen(true);
  };

  // Compute snapped time + percent given an absolute clientX and a track element.
  const computeSnapped = useCallback((clientX: number, trackEl: HTMLElement) => {
    const rect = trackEl.getBoundingClientRect();
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(1, x / rect.width));
    const { start, end } = getTimeWindow();
    const raw = new Date(start.getTime() + pct * (end.getTime() - start.getTime()));
    const snapped = snapToMinutes(raw, grid.snapMin);
    const snappedPct = ((snapped.getTime() - start.getTime()) / (end.getTime() - start.getTime())) * 100;
    return { snapped, snappedPct };
  }, [getTimeWindow, grid.snapMin]);

  // Manual drag: document-level mousemove + mouseup while a drag is in progress.
  useEffect(() => {
    if (!draggedItem) return;

    const onMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      if (!d.moved) {
        const dx = e.clientX - d.startX;
        const dy = e.clientY - d.startY;
        if (dx * dx + dy * dy < DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX) return;
        d.moved = true;
      }
      const { snapped, snappedPct } = computeSnapped(e.clientX, d.trackEl);
      setDropIndicator({ percent: snappedPct, iso: snapped.toISOString() });
    };

    const onUp = async (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      try {
        if (d.moved) {
          const { snapped } = computeSnapped(e.clientX, d.trackEl);
          if (snapped.toISOString() !== d.item.starts_at) {
            // A "slot" is the set of rows sharing (channel, action, starts_at) —
            // typically one row per audience. Move all of them so the whole slot
            // shifts as one, not just the variant the user happened to grab.
            try {
              // One backend op moves the whole slot (all audiences) and returns
              // an op_id so the move is undoable as a unit.
              const res = await moveSlot(
                d.item.channel,
                d.item.action,
                d.item.starts_at,
                snapped.toISOString(),
              );
              await load();
              if (res?.op_id) {
                const when = new Date(snapped).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
                setToast({ message: `Moved "${d.item.action}" to ${when}`, opId: res.op_id });
              }
            } catch (err) {
              console.error("Failed to move slot:", err);
            }
          } else {
            console.log("[EPG] drop = no-op (same slot)");
          }
        }
        // Note: a click-without-drag is intentionally a no-op now. To edit,
        // right-click → "Edit MediaPoint" on the context menu.
      } finally {
        dragRef.current = null;
        setDraggedItem(null);
        setDropIndicator(null);
      }
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [draggedItem, computeSnapped, load]);

  // Horizontal-scroll the timeline via wheel/trackpad and arrow keys.
  // Trackpad horizontal swipe → deltaX. Mouse wheel: hold Shift to convert
  // vertical wheel to horizontal pan. One pixel of accumulated delta per
  // PIXELS_PER_SNAP_STEP moves the window by one snap step.
  useEffect(() => {
    const el = gridPaperRef.current;
    if (!el) return;
    const PIXELS_PER_SNAP_STEP = 40;

    const onWheel = (e: WheelEvent) => {
      const horizontalIntent =
        e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY);
      if (!horizontalIntent) return;
      e.preventDefault();
      const delta = e.shiftKey ? e.deltaY || e.deltaX : e.deltaX;
      wheelAccumRef.current += delta;
      const steps = Math.trunc(wheelAccumRef.current / PIXELS_PER_SNAP_STEP);
      if (steps !== 0) {
        wheelAccumRef.current -= steps * PIXELS_PER_SNAP_STEP;
        setBaseTime((prev) => {
          const t = new Date(prev);
          t.setMinutes(t.getMinutes() + steps * grid.snapMin);
          return snapToMinutes(t, grid.snapMin);
        });
      }
    };

    // Track cursor time over any timeline track so keyboard shortcuts can
    // anchor zoom/center at the cursor.
    const findTrackForX = (clientX: number, clientY: number): { rect: DOMRect } | null => {
      const tracks = el.querySelectorAll<HTMLElement>(":scope > div > div > div:last-child");
      // The above selector looks for the per-row track. If structure shifts,
      // fall back to *any* element under the cursor inside the grid that has
      // children with `data-epg-item` (we don't tag those, so just use the
      // first track found whose rect contains clientX).
      for (const t of Array.from(tracks)) {
        const r = t.getBoundingClientRect();
        if (clientX >= r.left && clientX <= r.right) return { rect: r };
      }
      return null;
    };

    const onMouseMove = (e: MouseEvent) => {
      const found = findTrackForX(e.clientX, e.clientY);
      if (!found) return;
      const pct = Math.max(0, Math.min(1, (e.clientX - found.rect.left) / found.rect.width));
      const { start, end } = getTimeWindow();
      lastCursorTimeRef.current = new Date(start.getTime() + pct * (end.getTime() - start.getTime()));
    };

    const cursorOrMidWindow = (): Date => {
      if (lastCursorTimeRef.current) return lastCursorTimeRef.current;
      const { start, end } = getTimeWindow();
      return new Date((start.getTime() + end.getTime()) / 2);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      // Pan with arrows (existing behavior)
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        const sign = e.key === "ArrowLeft" ? -1 : 1;
        const minutes = e.shiftKey
          ? sign * currentZoom.hours * 60
          : e.altKey
            ? sign * grid.labelStepMin
            : sign * grid.snapMin;
        setBaseTime((prev) => {
          const t = new Date(prev);
          t.setMinutes(t.getMinutes() + minutes);
          return snapToMinutes(t, grid.snapMin);
        });
        return;
      }

      // Zoom in: + (shifted = key) or = key (unshifted)
      if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        zoomAtCursor(-1, cursorOrMidWindow());
        return;
      }
      // Zoom out: - or _
      if (e.key === "-" || e.key === "_") {
        e.preventDefault();
        zoomAtCursor(1, cursorOrMidWindow());
        return;
      }
      // Center view on cursor position
      if (e.key === "c" || e.key === "C") {
        e.preventDefault();
        centerOnCursor(cursorOrMidWindow());
        return;
      }
      // Jump to Now
      if (e.key === "n" || e.key === "N") {
        e.preventDefault();
        goToNow();
        return;
      }
    };

    // Wheel + mousemove are paper-local (cursor tracking only makes sense over the grid).
    // Keydown attaches to the document so users don't have to click the grid first.
    // Input/textarea typing is filtered inside onKeyDown.
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("mousemove", onMouseMove);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [grid.snapMin, grid.labelStepMin, currentZoom.hours, zoomLevel, baseTime, getTimeWindow]);

  // Calculate item position and width
  const getItemStyle = (item: EpgItemDto) => {
    const { start: windowStart, end: windowEnd } = getTimeWindow();
    const itemStart = new Date(item.starts_at);
    const itemEnd = new Date(item.ends_at);
    const windowDuration = windowEnd.getTime() - windowStart.getTime();
    const itemStartOffset = itemStart.getTime() - windowStart.getTime();
    const itemDuration = itemEnd.getTime() - itemStart.getTime();
    const left = (itemStartOffset / windowDuration) * 100;
    const width = (itemDuration / windowDuration) * 100;
    return {
      left: `${Math.max(0, left)}%`,
      width: `${Math.min(100 - Math.max(0, left), width)}%`,
      visible: left <= 100 && left + width >= 0,
    };
  };

  // Tick labels at clock-aligned boundaries within the visible window.
  const getTimeMarkers = () => {
    const markers: { position: number; label: string }[] = [];
    const { start, end } = getTimeWindow();
    const stepMs = grid.labelStepMin * 60_000;
    const windowDur = end.getTime() - start.getTime();
    const firstMs = Math.ceil(start.getTime() / stepMs) * stepMs;
    for (let t = firstMs; t <= end.getTime(); t += stepMs) {
      const time = new Date(t);
      markers.push({
        position: ((t - start.getTime()) / windowDur) * 100,
        label: time.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: currentZoom.hours <= 12,
        }),
      });
    }
    return markers;
  };

  // Vertical gridlines at every snap boundary.
  const getGridLines = () => {
    const lines: { position: number; major: boolean }[] = [];
    const { start, end } = getTimeWindow();
    const snapMs = grid.snapMin * 60_000;
    const labelMs = grid.labelStepMin * 60_000;
    const windowDur = end.getTime() - start.getTime();
    const firstMs = Math.ceil(start.getTime() / snapMs) * snapMs;
    for (let t = firstMs; t <= end.getTime(); t += snapMs) {
      lines.push({
        position: ((t - start.getTime()) / windowDur) * 100,
        major: t % labelMs === 0,
      });
    }
    return lines;
  };

  const gridLines = getGridLines();

  // Mediapoint body colors. Tuned Darwin-dark: the body just needs to be
  // visible against the gray-900 background; the policy strip at the bottom
  // carries the semantic signal in bright colors. "program" is the most
  // common kind and is intentionally near-neutral so 15 rows don't fry retinas.
  const getItemColor = (item: EpgItemDto) => {
    const colors: Record<string, string> = {
      blackout:   "#7f1d1d",   // deep red (rare, exceptional)
      allow:      "#14532d",   // deep green
      substitute: "#1e3a8a",   // deep blue
      program:    "#334155",   // gray-700-ish slate (calm)
      default:    "#475569",   // gray-600-ish
    };
    return colors[item.kind] || colors.default;
  };

  // Color for a matched broadcast policy's resolved action.
  const POLICY_ACTION_COLOR: Record<string, string> = {
    allow: "#16a34a",
    blackout: "#dc2626",
    substitute: "#2563eb",
    replace_cue: "#2563eb",
    program: "#8b5cf6",
    unknown: "#64748b",
  };
  const policyActionColor = (kind: string) =>
    POLICY_ACTION_COLOR[kind] || POLICY_ACTION_COLOR.unknown;

  // Item mousedown — start a potential drag. Find the parent track element.
  const onItemMouseDown = (e: React.MouseEvent, item: EpgItemDto, channel: string) => {
    if (e.button !== 0) return; // left button only
    if (!canWrite(channel)) return; // read-only channel: dragging disabled
    const itemEl = e.currentTarget as HTMLElement;
    const trackEl = itemEl.parentElement;
    if (!trackEl) return;
    e.preventDefault(); // suppress native text selection / image drag
    dragRef.current = {
      item,
      trackEl,
      channel,
      startX: e.clientX,
      startY: e.clientY,
      moved: false,
    };
    setDraggedItem(item);
    // Pre-seed indicator at the item's current start so something appears immediately
    const { snapped, snappedPct } = computeSnapped(e.clientX, trackEl);
    setDropIndicator({ percent: snappedPct, iso: snapped.toISOString() });
    console.log("[EPG] mousedown start drag", item.id);
  };

  const displayRows = channelParam ? rows.filter((r) => r.channel_name === channelParam) : rows;
  const detailItems = channelParam
    ? (displayRows[0]?.items ?? []).slice().sort((a, b) => a.starts_at.localeCompare(b.starts_at))
    : [];

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      {channelParam && (
        <Paper sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 2 }}>
          <Button component={RouterLink} to="/epg" size="small" variant="outlined" startIcon={<ChevronLeft />}>
            All channels
          </Button>
          <Typography variant="h6" sx={{ m: 0 }}>Channel: {channelParam}</Typography>
        </Paper>
      )}
      {/* Control Bar */}
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <IconButton onClick={() => slideTime('left')} title="Previous">
              <ChevronLeft />
            </IconButton>
            <Typography variant="body2" sx={{ minWidth: 120, textAlign: "center" }}>
              {baseTime.toLocaleDateString()} {baseTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </Typography>
            <IconButton onClick={() => slideTime('right')} title="Next">
              <ChevronRight />
            </IconButton>
            <Button size="small" variant="outlined" onClick={goToNow} startIcon={<AccessTime />}>
              Now
            </Button>
          </Box>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <IconButton onClick={() => handleZoom(-1)} disabled={zoomLevel === 0}>
              <ZoomIn />
            </IconButton>
            <ToggleButtonGroup
              value={zoomLevel}
              exclusive
              onChange={(e, val) => val !== null && setZoomLevel(val)}
              size="small"
            >
              {ZOOM_LEVELS.map((level, idx) => (
                <ToggleButton key={idx} value={idx}>
                  {level.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <IconButton onClick={() => handleZoom(1)} disabled={zoomLevel === ZOOM_LEVELS.length - 1}>
              <ZoomOut />
            </IconButton>
          </Box>

          <Button variant="contained" onClick={load} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </Button>

          <Typography variant="caption" color="text.secondary">
            Snap: {grid.snapMin} min · Ticks: every {grid.labelStepMin} min
          </Typography>
          <Tooltip
            arrow
            placement="bottom-start"
            title={
              <Box sx={{ fontSize: 12, lineHeight: 1.7 }}>
                <Box sx={{ fontWeight: 600, mb: 0.5 }}>Keyboard shortcuts</Box>
                <Box><kbd>+</kbd> / <kbd>−</kbd> &nbsp;zoom in / out</Box>
                <Box><kbd>c</kbd> &nbsp;center on cursor</Box>
                <Box><kbd>n</kbd> &nbsp;jump to Now</Box>
                <Box><kbd>←</kbd> / <kbd>→</kbd> &nbsp;pan one snap step</Box>
                <Box><kbd>Alt</kbd> + <kbd>←/→</kbd> &nbsp;pan one label step</Box>
                <Box><kbd>Shift</kbd> + <kbd>←/→</kbd> &nbsp;pan one full window</Box>
              </Box>
            }
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                color: "text.secondary",
                opacity: 0.7,
                cursor: "help",
                "&:hover": { opacity: 1 },
              }}
            >
              <KeyboardIcon sx={{ fontSize: 16 }} />
              <Typography variant="caption">Shortcuts</Typography>
            </Box>
          </Tooltip>



          {draggedItem && dropIndicator && (
            <Typography variant="caption" color="error.main" sx={{ fontWeight: 600 }}>
              → {new Date(dropIndicator.iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: currentZoom.hours <= 12 })}
            </Typography>
          )}
        </Box>
      </Paper>

      {/* EPG Grid */}
      <Paper sx={{ p: 2, overflow: "hidden", outline: "none" }} ref={(node) => { (containerRef as any).current = node; (gridPaperRef as any).current = node; }} tabIndex={0}>
        {/* Time Markers */}
        <Box sx={{ display: "flex", position: "relative", height: 30, mb: 1, pl: "150px" }}>
          {getTimeMarkers().map((marker, idx) => (
            <Box
              key={idx}
              sx={{
                position: "absolute",
                left: `calc(150px + ${marker.position}%)`,
                transform: "translateX(-50%)",
                fontSize: "11px",
                color: "text.secondary",
                whiteSpace: "nowrap",
              }}
            >
              {marker.label}
            </Box>
          ))}
        </Box>

        {/* Channel Rows */}
        {displayRows.map((row) => (
          <Box
            key={row.channel_id}
            sx={{ display: "flex", mb: 1, minHeight: 48, alignItems: "stretch" }}
          >
            <Box sx={{
              width: 150, pr: 2,
              display: "flex", alignItems: "center",
              borderRight: "1px solid", borderColor: "divider",
            }}>
              <Stack direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  component={RouterLink}
                  to={`/epg/${encodeURIComponent(row.channel_name)}`}
                  title={`Open ${row.channel_name} detail`}
                  sx={{ color: "inherit", textDecoration: "none", cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", "&:hover": { textDecoration: "underline" } }}
                >
                  {row.channel_name}
                </Typography>
                {!canWrite(row.channel_name) && (
                  <LockIcon sx={{ fontSize: 13, opacity: 0.5 }} titleAccess="Read-only for you" />
                )}
              </Stack>
            </Box>

            {/* Timeline Track */}
            <Box
              onContextMenu={(e) => {
                e.preventDefault();
                const rect = e.currentTarget.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const pct = Math.max(0, Math.min(1, x / rect.width));
                const { start, end } = getTimeWindow();
                const time = new Date(start.getTime() + pct * (end.getTime() - start.getTime()));
                setCtxMenu({ x: e.clientX, y: e.clientY, time, channel: row.channel_name });
              }}
              sx={{
                flex: 1,
                position: "relative",
                bgcolor: draggedItem ? "action.selected" : "action.hover",
                borderRadius: 1,
                ml: 1,
                transition: "background-color 0.2s, box-shadow 0.2s",
                ...(draggedItem && {
                  boxShadow: "inset 0 0 0 2px",
                  color: "primary.main",
                }),
              }}
            >
              {/* Snap gridlines (behind items) */}
              {gridLines.map((g, gi) => (
                <Box
                  key={`grid-${gi}`}
                  sx={{
                    position: "absolute",
                    left: `${g.position}%`,
                    top: 0,
                    bottom: 0,
                    width: "1px",
                    bgcolor: g.major ? "divider" : "action.disabledBackground",
                    opacity: g.major ? 0.7 : 0.35,
                    pointerEvents: "none",
                    zIndex: 0,
                  }}
                />
              ))}

              {row.items.map((item) => {
                const style = getItemStyle(item);
                const showText = currentZoom.showText;
                const color = getItemColor(item);
                if (!style.visible) return null;
                const isDragging = draggedItem?.id === item.id;
                const matched = item.matched_policies ?? [];
                const policyLines = matched.length
                  ? "\n\nPolicies (" + matched.length + "):\n" +
                    matched.map(m =>
                      (m.is_winner ? "★ " : "·  ") +
                      "[" + m.priority + "] " +
                      (m.enabled ? "" : "(disabled) ") +
                      m.name +
                      " — " + m.action_kind
                    ).join("\n")
                  : "\n\nNo policy applies to this slot.";
                const titleText =
                  `${item.action} for ${item.audience}\n` +
                  `${new Date(item.starts_at).toLocaleString()} - ${new Date(item.ends_at).toLocaleString()}\n` +
                  `Status: ${item.status}\n` +
                  `Right-click to edit · drag to move (snaps to ${grid.snapMin} min)` +
                  policyLines;

                return (
                  <div
                    key={item.id}
                    title={titleText}
                    onMouseDown={(e) => onItemMouseDown(e, item, row.channel_name)}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const trackEl = e.currentTarget.parentElement;
                      if (!trackEl) return;
                      const trackRect = trackEl.getBoundingClientRect();
                      const pct = Math.max(0, Math.min(1, (e.clientX - trackRect.left) / trackRect.width));
                      const { start, end } = getTimeWindow();
                      const time = new Date(start.getTime() + pct * (end.getTime() - start.getTime()));
                      setCtxMenu({ x: e.clientX, y: e.clientY, time, channel: row.channel_name, item });
                    }}
                    style={{
                      position: "absolute",
                      top: 4,
                      bottom: 4,
                      left: style.left,
                      width: style.width,
                      backgroundColor: color,
                      color: "white",
                      borderRadius: 4,
                      cursor: isDragging ? "grabbing" : "grab",
                      overflow: "hidden",
                      padding: showText ? "0 8px" : 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: showText ? "flex-start" : "center",
                      transition: "opacity 0.15s, box-shadow 0.15s",
                      opacity: isDragging ? 0.4 : 1,
                      userSelect: "none",
                      WebkitUserSelect: "none",
                      zIndex: 1,
                      fontSize: showText ? `${currentZoom.textSize}px` : 0,
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      boxSizing: "border-box",
                      // 1px hairline on left so adjacent items are separated;
                      // adds a 2px white ring when this item is being dragged.
                      boxShadow: isDragging
                        ? "inset 1px 0 0 rgba(255,255,255,0.14), 0 0 0 2px rgba(255,255,255,0.6)"
                        : "inset 1px 0 0 rgba(255,255,255,0.14)",
                    }}
                  >
                    {showText && (
                      <span style={{ pointerEvents: "none", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {item.action}
                      </span>
                    )}

                    {/* Policy overlay: bottom strip + count badge */}
                    {matched.length > 0 && (
                      <div
                        style={{
                          position: "absolute",
                          left: 2,
                          right: 2,
                          bottom: 2,
                          height: 3,
                          display: "flex",
                          gap: 1,
                          borderRadius: 1.5,
                          overflow: "hidden",
                          pointerEvents: "none",
                        }}
                      >
                        {matched.map((m) => (
                          <div
                            key={m.id}
                            style={{
                              flex: 1,
                              backgroundColor: policyActionColor(m.action_kind),
                              opacity: m.is_winner ? 1 : (m.enabled ? 0.45 : 0.2),
                              backgroundImage: m.is_winner
                                ? undefined
                                : "repeating-linear-gradient(45deg, transparent 0 2px, rgba(0,0,0,0.35) 2px 4px)",
                            }}
                          />
                        ))}
                      </div>
                    )}
                    {matched.length > 1 && (
                      <div
                        style={{
                          position: "absolute",
                          top: 2,
                          right: 2,
                          minWidth: 14,
                          height: 14,
                          padding: "0 3px",
                          borderRadius: 7,
                          backgroundColor: "rgba(0,0,0,0.55)",
                          color: "white",
                          fontSize: 9,
                          fontWeight: 700,
                          lineHeight: "14px",
                          textAlign: "center",
                          pointerEvents: "none",
                        }}
                      >
                        {matched.length}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Snapped drop-position indicator (visible while dragging) */}
              {draggedItem && dropIndicator && (
                <Box
                  sx={{
                    position: "absolute",
                    left: `${dropIndicator.percent}%`,
                    top: -4,
                    bottom: -4,
                    width: "2px",
                    bgcolor: "#ef4444",
                    boxShadow: "0 0 6px rgba(239,68,68,0.8)",
                    zIndex: 20,
                    pointerEvents: "none",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: -20,
                      left: 0,
                      transform: "translateX(-50%)",
                      bgcolor: "#ef4444",
                      color: "white",
                      fontSize: 11,
                      fontWeight: 600,
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 0.5,
                      whiteSpace: "nowrap",
                      lineHeight: 1.2,
                    }}
                  >
                    {new Date(dropIndicator.iso).toLocaleTimeString("en-US", {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: currentZoom.hours <= 12,
                    })}
                  </Box>
                </Box>
              )}

              {/* Current time indicator */}
              {(() => {
                const now = new Date();
                const { start, end } = getTimeWindow();
                if (now >= start && now <= end) {
                  const offset = ((now.getTime() - start.getTime()) / (end.getTime() - start.getTime())) * 100;
                  return (
                    <Box sx={{
                      position: "absolute",
                      left: `${offset}%`,
                      top: 0,
                      bottom: 0,
                      width: 2,
                      bgcolor: "error.main",
                      opacity: 0.4,
                      zIndex: 10,
                      pointerEvents: "none",
                    }} />
                  );
                }
                return null;
              })()}
            </Box>
          </Box>
        ))}

        {displayRows.length === 0 && !loading && (
          <Typography color="text.secondary" textAlign="center" py={4}>
            No EPG data available for this time period
          </Typography>
        )}
      </Paper>

      {channelParam && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            MediaPoints on {channelParam} ({detailItems.length})
          </Typography>
          {detailItems.length === 0 ? (
            <Typography color="text.secondary" variant="body2">
              No mediapoints in this time window.
            </Typography>
          ) : (
            <Box sx={{ display: "grid", gap: 0.25 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: "170px 1fr 110px 1.4fr", gap: 1, fontSize: 12, fontWeight: 700, color: "text.secondary", pb: 0.5, borderBottom: "1px solid", borderColor: "divider" }}>
                <span>Time</span><span>Action</span><span>Audience</span><span>Policies</span>
              </Box>
              {detailItems.map((it) => (
                <Box key={it.id} sx={{ display: "grid", gridTemplateColumns: "170px 1fr 110px 1.4fr", gap: 1, fontSize: 13, py: 0.5, borderBottom: "1px solid", borderColor: "divider", alignItems: "center" }}>
                  <span>
                    {new Date(it.starts_at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
                    {" \u2013 "}
                    {new Date(it.ends_at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                  <span style={{ fontWeight: 600 }}>{it.action}</span>
                  <span style={{ opacity: 0.85 }}>{it.audience}</span>
                  <span style={{ opacity: 0.9 }}>
                    {(it.matched_policies ?? []).length
                      ? (it.matched_policies ?? [])
                          .map((m) => `${m.is_winner ? "\u2605" : "\u00b7"} ${m.name} (${m.action_kind})`)
                          .join(", ")
                      : "none"}
                  </span>
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      )}

      <EpgEditor
        open={editorOpen}
        item={editing?.item ?? null}
        siblings={editing?.siblings}
        mode={editing?.mode}
        onClose={() => setEditorOpen(false)}
        onSaved={(t) => { load(); if (t) setToast(t); }}
      />

      {/* Right-click context menu on the timeline */}
      <Menu
        open={!!ctxMenu}
        onClose={() => setCtxMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={ctxMenu ? { left: ctxMenu.x, top: ctxMenu.y } : undefined}
        slotProps={{ paper: { sx: { minWidth: 220 } } }}
      >
        {ctxMenu?.item && [
          <MenuItem
            key="header-item"
            disabled
            sx={{ opacity: "0.8 !important", fontSize: 12, whiteSpace: "normal" }}
          >
            {ctxMenu.item.action} · {ctxMenu.item.audience}
          </MenuItem>,
          ...(canWrite(ctxMenu.item.channel)
            ? [
                <MenuItem
                  key="edit"
                  onClick={() => {
                    if (ctxMenu?.item) openEditorFor(ctxMenu.item);
                    setCtxMenu(null);
                  }}
                >
                  <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Edit MediaPoint</ListItemText>
                </MenuItem>,
                <Divider key="div-edit" />,
              ]
            : [
                <MenuItem key="ro" disabled sx={{ fontSize: 12, opacity: "0.7 !important" }}>
                  Read-only channel
                </MenuItem>,
                <Divider key="div-ro" />,
              ]),
        ]}
        {ctxMenu?.channel && canWrite(ctxMenu.channel) && (
          <MenuItem
            onClick={() => {
              if (ctxMenu) openEditorForNew(ctxMenu.channel!, ctxMenu.time);
              setCtxMenu(null);
            }}
          >
            <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Add MediaPoint here</ListItemText>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
              {ctxMenu.channel}
            </Typography>
          </MenuItem>
        )}
        {ctxMenu?.channel && canWrite(ctxMenu.channel) && <Divider />}
        <MenuItem disabled sx={{ opacity: "0.7 !important", fontSize: 12 }}>
          {ctxMenu?.time
            ? ctxMenu.time.toLocaleString("en-US", {
                month: "short", day: "numeric",
                hour: "2-digit", minute: "2-digit",
                hour12: currentZoom.hours <= 12,
              })
            : ""}
        </MenuItem>
        <Divider />
        <MenuItem
          disabled={zoomLevel === 0}
          onClick={() => {
            if (ctxMenu) zoomAtCursor(-1, ctxMenu.time);
            setCtxMenu(null);
          }}
        >
          <ListItemIcon><ZoomIn fontSize="small" /></ListItemIcon>
          <ListItemText>Zoom In</ListItemText>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            {zoomLevel > 0 ? ZOOM_LEVELS[zoomLevel - 1].label : "—"}
          </Typography>
        </MenuItem>
        <MenuItem
          disabled={zoomLevel === ZOOM_LEVELS.length - 1}
          onClick={() => {
            if (ctxMenu) zoomAtCursor(1, ctxMenu.time);
            setCtxMenu(null);
          }}
        >
          <ListItemIcon><ZoomOut fontSize="small" /></ListItemIcon>
          <ListItemText>Zoom Out</ListItemText>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            {zoomLevel < ZOOM_LEVELS.length - 1 ? ZOOM_LEVELS[zoomLevel + 1].label : "—"}
          </Typography>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (ctxMenu) centerOnCursor(ctxMenu.time);
            setCtxMenu(null);
          }}
        >
          <ListItemIcon><AccessTime fontSize="small" /></ListItemIcon>
          <ListItemText>Center here</ListItemText>
        </MenuItem>
      </Menu>

      <Snackbar
        open={!!toast}
        autoHideDuration={10000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={() => setToast(null)}
          action={<Button color="inherit" size="small" onClick={doUndo}>UNDO</Button>}
          sx={{ alignItems: "center" }}
        >
          {toast?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
